// ViewHeatMapTool.jsx
// Description: Tool for controlling heatmap opacity via a slider.

// Copyright HS Analysis GmbH, 2024
// Author: Viktor Eberhardt

import React from "react";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import Tool from "./Tool";

class ViewHeatMapTool extends Tool {
  constructor() {
    super();
    this.name = "View Heatmap";
    this.noConfig = false;
    this.initialOpacity = 100;
    this.setHeatMapOpacity = null;
  }

  /**
   * Sets the initial opacity and the setter function for the heat map layer.
   *
   * @param {Object} obj - The configuration object.
   * @param {number} [obj.heatMapOpacity] - The initial opacity value for the heat map (0-100).
   * @param {function} [obj.setHeatMapOpacity] - The function to update the heat map opacity.
   */
  setLayer(obj) {
    this.initialOpacity =
      typeof obj.heatMapOpacity === "number" ? obj.heatMapOpacity : 100;
    this.setHeatMapOpacity =
      typeof obj.setHeatMapOpacity === "function"
        ? obj.setHeatMapOpacity
        : null;
  }

  /**
   * Renders the configuration UI for the heatmap tool.
   *
   * Displays the tool name and a configuration form for adjusting the heatmap opacity.
   *
   * @returns {JSX.Element} The rendered configuration component.
   */
  renderConfiguration() {
    return (
      <div>
        <Typography variant="h6">{this.name}:</Typography>
        <HeatmapConfigForm
          initialOpacity={this.initialOpacity}
          setHeatMapOpacity={this.setHeatMapOpacity}
        />
      </div>
    );
  }
}

const HeatmapConfigForm = ({ initialOpacity, setHeatMapOpacity }) => {
  const [heatMapOpacity, setLocalHeatMapOpacity] =
    React.useState(initialOpacity);

  const handleChange = (_, value) => {
    setLocalHeatMapOpacity(value);
    if (setHeatMapOpacity) setHeatMapOpacity(value);
  };

  return (
    <div style={{ margin: "20px 20px 0 0" }}>
      <Typography gutterBottom>Heatmap Opacity</Typography>
      <Slider
        value={heatMapOpacity}
        min={0}
        max={100}
        step={1}
        valueLabelDisplay="auto"
        onChange={handleChange}
        aria-labelledby="heatmap-opacity-slider"
      />
    </div>
  );
};

HeatmapConfigForm.propTypes = {
  initialOpacity: PropTypes.number.isRequired,
  setHeatMapOpacity: PropTypes.func,
};

export default ViewHeatMapTool;
